<!--
  DistributorEdit -- SuperAdmin distributor edit page (PR-E + PR-F).

  Card: https://trello.com/c/sv7UGPvt (PR-E timezone)

  Route: /admin/distributors/:id/edit
  Gated on superadmin.access (the SA entry capability seeded by PR #1325).

  Functional model
  ================

  Loads a single Distributor row via the existing SAFRS GET and saves via the
  combined PATCH endpoint /api/superadmin/distributors/:id.

  The Company section mounts the SAME CompanyForm the Owner sees on Account >
  Company (IA unit U4, docs/planning/2026-10-19-distributor-setup-ia-proposal.md
  section 8). It saves as you go (Bug Intake #418): after a pause, every
  changed field goes out in one PATCH. The tax ID applies on its own confirm (write-only; last four
  shown). Nexus states keep their own on-change save. The SA-only sections
  (tier, Stripe, suppliers, access) are unchanged. The Company section is
  read-only for an actor without superadmin.settings.

  2026-08-27 (Jake): the "Default brand color" control was REMOVED
  from this page. UI-ONLY, exactly like the 2026-08-24 removal of the
  distributor-side card in
  pages/distributor/account/defaults/AccountDefaults.vue:
  `distributors.default_brand_color` still exists, still seeds a new
  store's `brand_color` at creation (api/src/models/store.py), is
  still read by distributor_summary, and is still writable through
  PATCH /api/superadmin/distributors/:id -- that endpoint keeps the
  field in `_WRITABLE_FIELDS` on purpose, since removing a control is
  not the same as removing an API capability. No UI edits it now. Do
  not "restore" the control on the theory that the value is orphaned;
  it is not.

  Note this page no longer sends `default_brand_color` AT ALL, rather
  than sending null. Sending null would CLEAR a stored default the SA
  can no longer see, on every unrelated timezone save.

  UI surface
  ==========

  Uses Vuetify (v-card / v-select / v-btn / v-alert) to match the
  distributor-side form pattern. main.js sets v-select/v-text-field
  defaults to variant=underlined, density=comfortable, so those props
  are inherited rather than repeated here. ASCII-only.

  Data flow
  =========

    1. on mount -> GET /api/distributors/:id/ (SAFRS, JSON:API)
    2. user edits Company fields (nothing is sent while typing)
    3. after a pause -> PATCH /api/superadmin/distributors/:id
                body { <only the changed fields> }
    4. on success, adopt what the server stored + show "Saved"; on failure,
       name it, keep the typed values, claim nothing

  Cancel returns to the SA distributors list via the router.
-->
<template>
    <v-card
        class="distributor-edit"
        data-testid="distributor-edit-root"
        elevation="0"
    >
        <v-card-title class="distributor-edit__header">
            <h1 class="distributor-edit__title">Edit Distributor</h1>
            <v-btn
                variant="text"
                data-testid="distributor-edit-cancel"
                @click="cancel"
            >
                Back to list
            </v-btn>
        </v-card-title>

        <v-card-text v-if="loading" data-testid="distributor-edit-loading">
            Loading distributor...
        </v-card-text>

        <v-alert
            v-else-if="loadError"
            type="error"
            variant="tonal"
            data-testid="distributor-edit-load-error"
        >
            {{ loadError }}
        </v-alert>

        <v-card-text v-else>
            <!--
              Company -- the SAME CompanyForm the Owner sees on Account >
              Company (IA unit U4, docs/planning/2026-10-19-distributor-setup-
              ia-proposal.md section 8): one set of fields, labels and rules
              for both audiences, so they cannot drift apart again.

              It SAVES AS YOU GO, like the store editor (Bug Intake #418, Jake
              2026-10-23): no Save button, an "All changes saved" status in the
              form. After a pause every changed field travels in ONE PATCH, so
              a rejected value leaves nothing written and the page never says
              saved for a request that failed. The form checks its own rules
              (a bad ZIP, an empty name) before sending anything.

              The tax ID is the one other write: CompanyForm applies it as
              soon as it is confirmed, through its own endpoint, and reports
              at the tax ID control.

              Nexus states are NOT part of the form. They are tax-compliance
              data with their own control, and a tick is the decision, so they
              keep committing on change in their own section below.
            -->
            <section
                class="distributor-edit__form"
                data-testid="distributor-edit-profile"
            >
                <h2
                    class="distributor-edit__access-title"
                    data-testid="distributor-edit-profile-heading"
                >
                    Company
                </h2>

                <p class="distributor-edit__hint">
                    The same details the distributor sees on their Company
                    page. Changes save as you go; a tax ID is applied as soon
                    as you confirm it.
                </p>

                <p
                    v-if="!canEditCompany"
                    class="distributor-edit__hint"
                    data-testid="distributor-edit-company-readonly"
                >
                    You can view this company's details but your role cannot
                    change them.
                </p>

                <CompanyForm
                    ref="companyForm"
                    :distributor="company"
                    :readonly="!canEditCompany"
                    :ein-distributor-id="resolvedDistributorId"
                    :save="saveCompany"
                    @update:field="onCompanyField"
                />

                <p
                    v-if="billingSyncWarning"
                    class="distributor-edit__error"
                    data-testid="distributor-edit-billing-sync-warning"
                >
                    {{ billingSyncWarning }}
                </p>
            </section>

            <section
                class="distributor-edit__form"
                data-testid="distributor-edit-nexus-section"
            >
                <div class="distributor-edit__row distributor-edit__row--wide">
                    <!--
                      Nexus is a CHECKBOX TABLE, matching TaxNexusEditor.vue
                      -- the Owner-facing surface for exactly this column.
                      Two surfaces editing one field should not present it two
                      different ways, and the per-jurisdiction `data-testid`
                      is what makes it driveable at the UI level.

                      NOT a v-autocomplete, which is what this shipped as
                      first. Vuetify renders a multi-select's options with a
                      checkbox GLYPH inside the option text, so the option
                      reads `check_box_outline_blankMaine` -- the ui-runner's
                      select_option does exact text matching and cannot pick
                      it. A plain checkbox is driven by `click`, needs no
                      runner primitive, and is the pattern already proven on
                      the Owner side.

                      Each box commits on CHANGE -- ticking a box IS the
                      decision, and there is no half-typed state to protect.
                    -->
                    <h2 class="distributor-edit__access-title">Nexus states</h2>
                    <NexusStatesGrid
                        :model-value="nexusDraft"
                        :disabled="!canEditCompany"
                        legend=""
                        root-testid="distributor-edit-nexus"
                        item-testid-prefix="distributor-edit-nexus-"
                        @update:model-value="onNexusChange"
                    />
                    <p class="distributor-edit__hint">
                        Where this distributor collects sales tax. They are
                        responsible for determining their own nexus in each
                        state. Ticking a state saves it right away.
                    </p>
                    <span
                        v-if="nexusSaved"
                        class="distributor-edit__saved"
                        data-testid="distributor-edit-nexus-saved"
                    >
                        Saved
                    </span>
                    <span
                        v-if="nexusError"
                        class="distributor-edit__error"
                        data-testid="distributor-edit-nexus-error"
                    >
                        {{ nexusError }}
                    </span>
                </div>
            </section>

            <!--
              Subscription tier.

              Jake, 2026-08-31: "On the Distributor Detail Page (DDP) in the
              SA app, there's no way to assign a subscription tier to a
              distributor. Please add that above Supplier availability."

              ABOVE Supplier availability, and that ordering is the design,
              not a preference: the tier DETERMINES the supplier list
              rendered below it. Putting the cause under the effect would
              read as an afterthought to the list it governs.

              REWORKED 2026-10-20 (Jake): the section names the PLAN the
              distributor is pinned to ("Plan: Test Plan") with the tier as
              secondary context, and the picker lists real PLANS (the same
              GET /plans the Plans & Pricing page reads) marked Default /
              Hidden. It used to show the TIER name as if it were the plan
              and map a picked tier to its default plan, which could move a
              distributor off the plan it was on without saying so. Saving now
              asks "from plan -> to plan" first.

              THE NO-TIER STATE IS SPELLED OUT. 5 of 18 distributors on the
              8083 sandbox had no subscription row at all, and those are
              precisely the ones showing no available suppliers. An empty
              select would be absence with no explanation. Saving still
              works for them -- the endpoint creates the subscription
              (_create_distributor_subscription).

              Its own Save, outside the timezone form, for the same reason
              the Access and Supplier sections are: this is a separate write,
              and a half-finished timezone edit must not be able to carry a
              tier change with it.
            -->
            <section
                class="distributor-edit__tier"
                data-testid="distributor-edit-tier"
            >
                <h2 class="distributor-edit__access-title">
                    Subscription plan
                </h2>

                <p
                    v-if="tierLoading"
                    data-testid="distributor-edit-tier-loading"
                >
                    Loading subscription tier...
                </p>

                <template v-else>
                    <template v-if="tierAssigned && currentPlan">
                        <p data-testid="distributor-edit-plan-current">
                            Plan:
                            <strong>{{ currentPlan.name }}</strong>
                            <span v-if="currentPlan.is_disabled">
                                (disabled)
                            </span>
                        </p>
                    </template>
                    <p
                        v-else
                        class="distributor-edit__tier-none"
                        data-testid="distributor-edit-tier-none"
                    >
                        No plan has been assigned to this distributor. The
                        suppliers listed below are a fallback, not a choice
                        anyone made for them -- pick a plan to set one.
                    </p>

                    <p
                        class="distributor-edit__hint"
                        data-testid="distributor-edit-tier-help"
                    >
                        The plan decides which suppliers this distributor
                        can sell from. Changing the plan can replace the
                        supplier list below, except where a supplier has been
                        overridden for this distributor specifically.
                    </p>

                    <p
                        v-if="tierError"
                        class="distributor-edit__error"
                        data-testid="distributor-edit-tier-error"
                    >
                        {{ tierError }}
                    </p>

                    <!--
                      A v-select (the ui-runner's `select_option` drives
                      Vuetify via `input[role=combobox]` and cannot operate a
                      native <select>). Items are the REAL plans from the same
                      list the Plans & Pricing page uses, never tier names.
                    -->
                    <div class="distributor-edit__tier-controls">
                        <v-select
                            :model-value="planDraft"
                            :items="planItems"
                            item-title="label"
                            item-value="value"
                            label="Plan"
                            class="distributor-edit__tier-select"
                            data-testid="distributor-edit-tier-select"
                            @update:modelValue="onPlanPicked"
                        />

                        <v-btn
                            color="primary"
                            :disabled="tierSaving || !planDirty"
                            :loading="tierSaving"
                            data-testid="distributor-edit-tier-save"
                            @click="requestPlanChange"
                        >
                            {{ tierSaving ? "Saving..." : "Save plan" }}
                        </v-btn>
                        <span
                            v-if="tierSaved"
                            class="distributor-edit__saved"
                            data-testid="distributor-edit-tier-saved"
                        >
                            Saved.
                        </span>
                    </div>

                    <div
                        v-if="planConfirming"
                        class="distributor-edit__tier-confirm"
                        data-testid="distributor-edit-tier-confirm-panel"
                    >
                        <p data-testid="distributor-edit-tier-confirm-text">
                            Move this distributor from
                            <strong>{{ currentPlan ? currentPlan.name : "no plan" }}</strong>
                            to
                            <strong>{{ draftPlanName }}</strong>?
                        </p>
                        <v-btn
                            color="primary"
                            :disabled="tierSaving"
                            data-testid="distributor-edit-tier-confirm"
                            @click="savePlan"
                        >
                            Confirm move
                        </v-btn>
                        <v-btn
                            variant="text"
                            :disabled="tierSaving"
                            data-testid="distributor-edit-tier-cancel"
                            @click="cancelPlanChange"
                        >
                            Cancel
                        </v-btn>
                    </div>
                </template>
            </section>

            <!-- SA comp / uncomp (PR-C). Keyed on the tier so assigning a
                 tier above reloads it out of the "no subscription" state. -->
            <SubscriptionCompCard
                v-if="resolvedDistributorId && !tierLoading"
                :key="`comp-${tierAssigned && currentPlan ? currentPlan.id : 'none'}`"
                :distributor-id="resolvedDistributorId"
            />

            <!-- Per-distributor MSA launch gate bypass (F2, Jake 2026-10-21):
                 staff only, reason required, audited. -->
            <MsaGateBypassCard
                v-if="resolvedDistributorId"
                :distributor-id="resolvedDistributorId"
            />

            <!--
              Stripe platform mode (dual-mode unit 5).

              Jake, 2026-09-17: let real distributors run in production mode
              while others are still tested in sandbox mode. Connect OAuth
              connects to the LIVE account by default, UNLESS the SuperAdmin
              has marked that distributor as sandbox.

              THE ORDER IS THE FEATURE. A connected Stripe account belongs to
              exactly ONE platform. Changing the mode while an account is
              attached leaves a live acct_ id addressed with a sandbox key --
              or the reverse -- which does not fail here. It fails later, at a
              real charge, as an opaque Stripe error on a surface that has
              nothing to do with this page.

              So while an account is connected there is NO select and NO save
              button: not a disabled one, absent. A disabled control is an
              affordance someone re-enables in devtools; an absent one is not.
              The endpoint enforces the same rule with a 409 -- that is the
              guarantee, this is the hint, and both exist because either alone
              would be wrong (a UI-only guard is one curl from bypassed, a
              backend-only guard is a mystery error).

              Deliberately NOT an auto-disconnect on switch. Tearing down a
              real Stripe binding as a side effect of a dropdown is exactly the
              invisible consequence this project's rules forbid; the locked
              panel tells the admin what to do and they do it deliberately.

              The mode stays READABLE while locked. "You may not change this"
              and "you may not see this" are different rules and only the first
              is ours -- knowing a tenant is on the live platform is much of
              why an SA opens this page.

              A v-select, not a native <select>: `select_option` in the
              ui-runner drives a Vuetify overlay via input[role=combobox] and
              cannot operate a native select at all
              (tools/ui-runner/ui_runner/browser.py), so a plain select would
              make this untestable at the UI level -- the same reasoning the
              tier picker above records.
            -->
            <section
                class="distributor-edit__stripe-mode"
                data-testid="distributor-edit-stripe-mode"
            >
                <h2 class="distributor-edit__access-title">
                    Stripe platform
                </h2>

                <p
                    v-if="stripeModeLoading"
                    data-testid="distributor-edit-stripe-mode-loading"
                >
                    Loading Stripe platform mode...
                </p>

                <template v-else>
                    <p data-testid="distributor-edit-stripe-mode-current">
                        This distributor transacts on the
                        <strong>{{ stripeModeLabel }}</strong> Stripe platform.
                    </p>

                    <!--
                      The locked state. Renders only when the server reports a
                      connected account, and it is the element that carries the
                      disconnect-first instruction -- its words are asserted by
                      both the component spec and the ui-runner scenario, so a
                      refactor cannot keep the testid and drop the guidance.
                    -->
                    <p
                        v-if="stripeAccountConnected"
                        class="distributor-edit__hint"
                        data-testid="distributor-edit-stripe-mode-locked"
                    >
                        This distributor has a Stripe account connected, so the
                        platform cannot be changed. Changing it now would leave
                        the connected account pointing at the wrong Stripe
                        platform and every charge would fail. To move them:
                        disconnect the Stripe account first, then change the
                        platform here, then ask them to reconnect with Stripe
                        Connect.
                    </p>

                    <template v-else>
                        <p
                            class="distributor-edit__hint"
                            data-testid="distributor-edit-stripe-mode-help"
                        >
                            Which ThreadFlow Stripe platform this distributor's
                            Connect account and charges run against. Set this
                            BEFORE they connect with Stripe Connect -- it
                            decides which platform they connect to.
                        </p>

                        <p
                            v-if="stripeModeError"
                            class="distributor-edit__error"
                            data-testid="distributor-edit-stripe-mode-error"
                        >
                            {{ stripeModeError }}
                        </p>

                        <div class="distributor-edit__tier-controls">
                            <v-select
                                :model-value="stripeModeDraft"
                                :items="stripeModeItems"
                                item-title="label"
                                item-value="value"
                                label="Stripe platform"
                                class="distributor-edit__tier-select"
                                data-testid="distributor-edit-stripe-mode-select"
                                @update:modelValue="onStripeModePicked"
                            />

                            <v-btn
                                color="primary"
                                :disabled="stripeModeSaving || !stripeModeDirty"
                                :loading="stripeModeSaving"
                                data-testid="distributor-edit-stripe-mode-save"
                                @click="saveStripeMode"
                            >
                                {{
                                    stripeModeSaving
                                        ? "Saving..."
                                        : "Save platform"
                                }}
                            </v-btn>
                            <span
                                v-if="stripeModeSaved"
                                class="distributor-edit__saved"
                                data-testid="distributor-edit-stripe-mode-saved"
                            >
                                Saved.
                            </span>
                        </div>

                        <!--
                          The stranded-order WARNING (Jake, 2026-09-28: "Warn,
                          don't refuse"). Batched orders pin the shopper's
                          saved card to the CURRENT Stripe platform; after a
                          switch the charger looks it up on the other one and
                          holds the order forever. The server reports how many
                          orders that is and refuses until the admin confirms
                          THAT NUMBER -- confirm echoes it back, so a count
                          that grew in the meantime is shown again instead of
                          being waved through.
                        -->
                        <div
                            v-if="stripeModeUncollectable !== null"
                            class="distributor-edit__error"
                            data-testid="distributor-edit-stripe-mode-uncollectable"
                        >
                            <p>
                                <strong
                                    data-testid="distributor-edit-stripe-mode-uncollectable-count"
                                    >{{ stripeModeUncollectable }}</strong
                                >
                                unpaid
                                {{
                                    stripeModeUncollectable === 1
                                        ? "order has a saved card"
                                        : "orders have a saved card"
                                }}
                                that was saved on the current Stripe platform.
                                After this change those saved cards cannot be
                                charged, so those orders will be held and never
                                collected. Nothing has been changed yet.
                            </p>
                            <v-btn
                                color="error"
                                :disabled="stripeModeSaving"
                                :loading="stripeModeSaving"
                                data-testid="distributor-edit-stripe-mode-uncollectable-confirm"
                                @click="confirmStripeModeUncollectable"
                            >
                                Change platform anyway
                            </v-btn>
                            <v-btn
                                variant="text"
                                :disabled="stripeModeSaving"
                                data-testid="distributor-edit-stripe-mode-uncollectable-cancel"
                                @click="cancelStripeModeUncollectable"
                            >
                                Cancel
                            </v-btn>
                        </div>
                    </template>
                </template>
            </section>

            <!--
              Stripe connections (park / switch). Beside the mode control
              because it is the way past that control's lock: the lock says
              "disconnect first", which revokes the account on Stripe's side;
              park moves it aside and keeps it restorable. `changed` re-reads
              the mode section, which reads a different endpoint and would
              otherwise show the pre-move mode until a reload.
            -->
            <StripeBindingsCard
                v-if="stripeBindingsDistributorId !== null"
                :distributor-id="stripeBindingsDistributorId"
                @changed="loadStripeMode"
            />

            <!--
              Supplier availability override.

              Jake, 2026-08-27: "We're also going to want to be able to
              override the available suppliers for a specific distributor,
              and I think that belongs on the DDP."

              WHY THIS BELONGS HERE, when brand color did not
              ==============================================

              The bar on this page is high -- `default_brand_color` was
              removed from it on 2026-08-24 as not belonging. This control
              earns its place by being precisely what the DDP is for: a
              PER-DISTRIBUTOR OVERRIDE of a PLATFORM DEFAULT. The platform
              default (which suppliers a plan includes) is set on
              /admin/plans-pricing; this is where one distributor is given a
              different answer.

              OUTSIDE the timezone form, like the Access section and for the
              same reason: it is its own write with its own Save, so a
              half-finished timezone edit cannot carry a supplier grant with
              it, and an abandoned form cannot leave the SA believing they
              granted a supplier they did not.

              Each row shows what the TIER says next to the override, because
              an SA who cannot see the inherited value cannot tell a
              deliberate override from a default they are about to clobber.
              "Use plan default" is a first-class action, not an afterthought:
              without a way back, every visit to this page ratchets one more
              distributor permanently off the tier list, and a later tier
              change silently stops reaching them.
            -->
            <section
                class="distributor-edit__suppliers"
                data-testid="distributor-edit-suppliers"
            >
                <h2 class="distributor-edit__access-title">
                    Supplier availability
                </h2>

                <p
                    class="distributor-edit__hint"
                    data-testid="distributor-edit-suppliers-help"
                >
                    These suppliers come from the distributor's plan<span
                        v-if="currentPlan"
                    >
                        (<strong>{{ currentPlan.name }}</strong
                        >)</span
                    >. Overriding one here affects only this distributor.
                </p>

                <p
                    v-if="supplierError"
                    class="distributor-edit__error"
                    data-testid="distributor-edit-suppliers-error"
                >
                    {{ supplierError }}
                </p>

                <p
                    v-if="supplierLoading"
                    data-testid="distributor-edit-suppliers-loading"
                >
                    Loading supplier availability...
                </p>

                <template v-else>
                    <table class="distributor-edit__supplier-table">
                        <thead>
                            <tr>
                                <th>Supplier</th>
                                <th>Plan default</th>
                                <th>Available</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="supplier in supplierRows"
                                :key="supplier.supplier_key"
                                :data-testid="
                                    'distributor-edit-supplier-row-' +
                                    supplier.supplier_key
                                "
                            >
                                <td>
                                    {{ supplier.name }}
                                    <div class="distributor-edit__muted">
                                        {{ supplier.supplier_key }}
                                    </div>
                                </td>
                                <td>
                                    {{ supplier.tier_enabled ? "On" : "Off" }}
                                </td>
                                <td>
                                    <input
                                        type="checkbox"
                                        :checked="supplier.effective"
                                        :data-testid="
                                            'distributor-edit-supplier-toggle-' +
                                            supplier.supplier_key
                                        "
                                        @change="
                                            onSupplierToggle(supplier, $event)
                                        "
                                    />
                                </td>
                                <td>
                                    <!--
                                      Only offered when an override actually
                                      exists. Showing it on a row that is
                                      already inheriting would be a control
                                      that does nothing.
                                    -->
                                    <button
                                        v-if="supplier.override !== null"
                                        type="button"
                                        class="distributor-edit__link-btn"
                                        :data-testid="
                                            'distributor-edit-supplier-clear-' +
                                            supplier.supplier_key
                                        "
                                        @click="clearSupplierOverride(supplier)"
                                    >
                                        Use plan default
                                    </button>
                                    <span
                                        v-else
                                        class="distributor-edit__muted"
                                        :data-testid="
                                            'distributor-edit-supplier-inherits-' +
                                            supplier.supplier_key
                                        "
                                    >
                                        Inherits plan
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div class="distributor-edit__actions">
                        <v-btn
                            color="primary"
                            :disabled="supplierSaving || !supplierDirty"
                            :loading="supplierSaving"
                            data-testid="distributor-edit-suppliers-save"
                            @click="saveSupplierOverrides"
                        >
                            {{
                                supplierSaving
                                    ? "Saving..."
                                    : "Save supplier availability"
                            }}
                        </v-btn>
                        <span
                            v-if="supplierSaved"
                            class="distributor-edit__saved"
                            data-testid="distributor-edit-suppliers-saved"
                        >
                            Saved.
                        </span>
                    </div>
                </template>
            </section>

            <!--
              Tenant access (enable / disable). DELIBERATELY OUTSIDE the form
              above: the timezone field is drafted and committed by Save,
              whereas disabling takes effect the moment it is confirmed
              and locks every user of the tenant out.
              Folding it into the form would let a half-finished timezone edit
              carry a lockout with it, and would let an abandoned form leave
              the SA believing they had disabled a tenant they had not.

              Card: https://trello.com/c/qJidTXSJ
            -->
            <section
                v-if="canManageTenants"
                class="distributor-edit__access"
                data-testid="distributor-edit-access"
            >
                <h2 class="distributor-edit__access-title">Access</h2>

                <p data-testid="distributor-edit-access-state">
                    This distributor is currently
                    <strong>{{ isDisabled ? "disabled" : "active" }}</strong
                    >.
                </p>

                <p
                    class="distributor-edit__hint"
                    data-testid="distributor-edit-access-help"
                >
                    Disabling a distributor stops everyone at that company from
                    signing in, takes its storefronts offline, and suspends its
                    billing. Enabling it again restores access and returns
                    billing to exactly the state it was in. Orders, stores, and
                    campaigns are never changed either way.
                </p>

                <v-alert
                    v-if="accessError"
                    type="error"
                    variant="tonal"
                    class="distributor-edit__feedback"
                    data-testid="distributor-edit-disable-error"
                >
                    {{ accessError }}
                </v-alert>

                <v-btn
                    :color="isDisabled ? 'primary' : 'error'"
                    variant="outlined"
                    :disabled="accessPending"
                    :loading="accessPending"
                    data-testid="distributor-edit-disable"
                    @click="onAccessAction"
                >
                    {{ isDisabled ? "Enable distributor" : "Disable distributor" }}
                </v-btn>
            </section>
        </v-card-text>

        <!--
          Only the DISABLE direction is confirmation-gated. Re-enabling is not
          the dangerous direction, and gating it too would train an SA to click
          through the dialog that actually matters.
        -->
        <v-dialog
            v-if="confirmOpen"
            :model-value="true"
            max-width="480"
            persistent
        >
            <v-card data-testid="distributor-edit-disable-confirm">
                <v-card-title>Disable {{ distributorName }}?</v-card-title>
                <v-card-text>
                    Nobody at <strong>{{ distributorName }}</strong> will be
                    able to sign in, its storefronts will stop serving
                    shoppers, and its billing will be suspended.
                    <br /><br />
                    This is reversible -- enabling the distributor again
                    restores access and returns billing to exactly the state it
                    is in now.
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn
                        variant="text"
                        data-testid="distributor-edit-disable-confirm-no"
                        @click="cancelAccessAction"
                    >
                        Cancel
                    </v-btn>
                    <v-btn
                        color="error"
                        variant="flat"
                        data-testid="distributor-edit-disable-confirm-yes"
                        @click="confirmAccessAction"
                    >
                        Disable distributor
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import axios from "@/axios";
import { US_JURISDICTIONS } from "@/data/usStates";
import SubscriptionCompCard from "@/pages/superadmin/SubscriptionCompCard.vue";
import MsaGateBypassCard from "@/pages/superadmin/MsaGateBypassCard.vue";
import NexusStatesGrid from "@/pages/distributor/account/compliance/NexusStatesGrid.vue";
import { setDistributorDisabled } from "@/services/distributorService";
import {
    getDistributorSupplierOverrides,
    listPlans,
    listTiers,
    reassignDistributorPlan,
    setDistributorSupplierOverrides,
} from "@/services/superadminPricingService";
import {
    getStripePlatformMode,
    setStripePlatformMode,
} from "@/services/superadminStripeModeService";
import { useAccountStore } from "@/stores/account";
import StripeBindingsCard from "@/components/superadmin/StripeBindingsCard.vue";
import CompanyForm from "@/pages/distributor/account/CompanyForm.vue";
import { COMPANY_FIELDS } from "@/pages/distributor/account/companyFields";

export default {
    name: "DistributorEdit",

    components: {
        StripeBindingsCard,
        NexusStatesGrid,
        CompanyForm,
        SubscriptionCompCard,
        MsaGateBypassCard,
    },

    props: {
        // Route param `:id` is passed in by vue-router via `props: true`
        // OR read from `$route.params.id`. We accept both for testability:
        // tests can mount with `props: { id: '42' }` without configuring
        // the full router. Runtime falls back to the route param.
        id: {
            type: [String, Number],
            default: null,
        },
    },

    setup(props) {
        const loading = ref(true);
        const loadError = ref("");
        const distributorName = ref("");

        // ---- Company: the shared CompanyForm, ONE save ------------------
        // IA unit U4. The record CompanyForm edits lives here as `company`;
        // the form never mutates it, it asks us to via `update:field`.
        // `committedCompany` is the last state the SERVER confirmed, and
        // dirtiness is measured against THAT, so Save sends exactly what
        // changed (the endpoint writes only the keys PRESENT, so sending an
        // untouched field -- even as null -- would clear a stored value).
        function companyFromAttrs(attrs) {
            const record = {
                // The legacy free-text address: shown by the form as a
                // read-only hint for re-entry, NEVER written from here.
                address: attrs.address ?? "",
            };
            for (const field of COMPANY_FIELDS) {
                record[field] =
                    field === "timezone"
                        ? (attrs.timezone ?? null)
                        : (attrs[field] ?? "");
            }
            return record;
        }

        // null, undefined and "" are the same "not set" for dirtiness.
        function norm(value) {
            return value === null || value === undefined ? "" : String(value);
        }

        const companyForm = ref(null);
        const company = ref(companyFromAttrs({}));
        const committedCompany = ref(companyFromAttrs({}));
        // Non-blocking: set when the billing contact saved but the Stripe
        // billing Customer could not be updated (response `warnings`).
        const billingSyncWarning = ref("");

        // ---- Nexus: auto-save on change ---------------------------------
        const nexusDraft = ref([]);
        const nexusCommitted = ref([]);
        const nexusSaving = ref(false);
        const nexusSaved = ref(false);
        const nexusError = ref("");

        // Full US state NAMES, not abbreviations. Load-bearing: the
        // sales-tax consumer compares against `get_state_name(region)`
        // (api/src/endpoints/sales_tax.py), so an abbreviation stored here
        // silently reads as "no nexus" and tax stops being collected.
        // Sourced from US_JURISDICTIONS -- the same single source of truth
        // TaxNexusEditor.vue renders, documented as matching the backend
        // canonical list in api/src/helper_tables/us_state_codes.csv. A
        // second list here would be a second source that one day disagrees.
        const stateNames = computed(() =>
            US_JURISDICTIONS.map((j) => j.name),
        );

        // ---- Tenant access (enable / disable) --------------------------
        // Card: https://trello.com/c/qJidTXSJ
        //
        // Entry point 2 of 2 for the same action -- the SA distributor list
        // (pages/distributor/account/DistributorList.vue) is the other. BOTH
        // go through `setDistributorDisabled`; neither POSTs for itself.
        // Two entry points, one write model.
        //
        // `disabledAt` mirrors distributors.disabled_at: null = active.
        const disabledAt = ref(null);
        const confirmOpen = ref(false);
        const accessPending = ref(false);
        const accessError = ref("");

        // Set by a successful write in this session; holds the SERVER's
        // reported boolean and takes precedence over `disabledAt`, which is
        // only refreshed by a reload.
        //
        // Why a separate ref rather than writing `disabledAt`: the endpoints
        // return {id, changed, disabled} with NO timestamp, so there is
        // nothing truthful to put in a field named for a server timestamp.
        // Fabricating `new Date()` there would store a value the server never
        // produced -- harmless while only its truthiness is read, and a real
        // bug the day someone renders it.
        const disabledOverride = ref(null);

        const isDisabled = computed(() =>
            typeof disabledOverride.value === "boolean"
                ? disabledOverride.value
                : Boolean(disabledAt.value),
        );

        // This ROUTE is gated on `superadmin.access`, but the disable /
        // restore endpoints require `superadmin.tenants`. An actor can
        // therefore legitimately reach this page without being able to act,
        // so the control is hidden for them rather than rendered into a
        // guaranteed 403. The server gate remains the real authority; this is
        // only about not offering an action that cannot succeed.
        //
        // Resolved lazily and defensively. `useAccountStore()` throws when no
        // Pinia is active, and this page is mounted in contexts that do not
        // install one. A capability READ must never be able to blank the whole
        // edit form -- failing closed here hides one optional control, which
        // is strictly better than losing the timezone surface.
        // The server gate is the real authority either way.
        function readCapabilities() {
            try {
                const caps = useAccountStore().capabilities;
                return Array.isArray(caps) ? caps : [];
            } catch (e) {
                return [];
            }
        }
        const canManageTenants = computed(() =>
            readCapabilities().includes("superadmin.tenants"),
        );

        // Reactive-friendly id for child components (the EIN field loads its
        // status for this distributor).
        const resolvedDistributorId = computed(() => resolveId());

        function resolveId() {
            if (props.id !== null && props.id !== undefined) return props.id;
            // Defensive: vue-router populates $route.params even in
            // composition API via the implicit `this` proxy; tests
            // that pass `id` via props bypass this.
            // eslint-disable-next-line no-undef
            if (typeof window !== "undefined" && window.location) {
                const m = window.location.pathname.match(
                    /\/admin\/distributors\/(\d+)\/edit/
                );
                if (m) return m[1];
            }
            return null;
        }

        async function load() {
            loading.value = true;
            loadError.value = "";
            const id = resolveId();
            if (id === null) {
                loadError.value = "No distributor id supplied.";
                loading.value = false;
                return;
            }
            try {
                const resp = await axios.get(`/api/distributors/${id}/`);
                // SAFRS JSON:API shape: data.data.attributes
                const attrs =
                    resp?.data?.data?.attributes ?? resp?.data ?? {};
                distributorName.value = attrs.name || `Distributor #${id}`;
                adoptServerState(attrs);
                // `disabled_at` is a plain nullable column on Distributor and
                // is NOT in `exclude_attrs`, so SAFRS serializes it here.
                disabledAt.value = attrs.disabled_at || null;
                // A fresh read supersedes any in-session override.
                disabledOverride.value = null;
            } catch (e) {
                loadError.value = "Could not load distributor.";
            } finally {
                loading.value = false;
            }
        }

        /** Seed the Company record, its baseline and nexus from a server row. */
        function adoptServerState(attrs) {
            company.value = companyFromAttrs(attrs);
            committedCompany.value = companyFromAttrs(attrs);
            // A distributor predating the field has NULL nexus. Coerce to []
            // so the grid never receives null.
            const nexus = Array.isArray(attrs.nexus_states)
                ? [...attrs.nexus_states]
                : [];
            nexusDraft.value = [...nexus];
            nexusCommitted.value = [...nexus];
        }

        // The Company section is read-only for an actor who can reach this
        // page (superadmin.access) but cannot write it: the PATCH requires
        // superadmin.settings and would 404 for them. Hiding Save rather than
        // rendering it into a guaranteed failure; the server gate remains the
        // real authority. Failing closed on an unreadable store (see
        // readCapabilities) leaves the form read-only, never blank.
        const canEditCompany = computed(() =>
            readCapabilities().includes("superadmin.settings"),
        );

        /** CompanyForm asks us to change a field; it never mutates the record. */
        function onCompanyField({ key, value }) {
            if (!COMPANY_FIELDS.includes(key)) return;
            company.value = { ...company.value, [key]: value };
        }

        /** The server's message when it sent a plain string, else "". */
        function serverMessage(err) {
            const data = err?.response?.data;
            for (const key of ["error", "message"]) {
                if (typeof data?.[key] === "string" && data[key].trim()) {
                    return data[key].trim();
                }
            }
            return "";
        }

        /** Name what went wrong. Always ends by saying nothing was changed. */
        function describeCompanyFailure(err) {
            const status = err?.response?.status;
            let reason;
            if (status === 409) {
                reason = "That name is already taken by another distributor.";
            } else if (status === 403 || status === 404) {
                reason =
                    "Permission denied. You may not edit this distributor's company details.";
            } else if (status === 400) {
                const detail = serverMessage(err);
                reason = detail
                    ? `A value was rejected: ${detail}`
                    : "A value was rejected. Please check it and try again.";
            } else {
                reason = "Could not save the company details.";
            }
            return `${reason} Nothing was changed.`;
        }

        /**
         * The Company form's autosave: every changed field, one PATCH. The
         * endpoint validates the whole payload before writing any of it, so a
         * rejection leaves nothing half-applied -- and this only ever returns
         * ok after a 2xx. CompanyForm has already run the field rules.
         *
         * On failure the typed values stay (the SA watched themselves make
         * those edits) and the baseline does not advance, so the next save
         * carries the same fields again. An edit typed while the request was
         * in flight is kept and goes out next, never overwritten by the
         * response. Never logs a field value.
         *
         * Resolves { ok: true } or { ok: false, error }; never throws.
         */
        async function saveCompany() {
            if (!canEditCompany.value) {
                return { ok: false, error: describeCompanyFailure({ response: { status: 403 } }) };
            }
            const payload = {};
            for (const field of COMPANY_FIELDS) {
                if (
                    norm(company.value[field]) !==
                    norm(committedCompany.value[field])
                ) {
                    payload[field] = company.value[field];
                }
            }
            // Edited and put back: nothing to send, and nothing to claim.
            if (Object.keys(payload).length === 0) return { ok: true };

            try {
                const resp = await axios.patch(
                    `/api/superadmin/distributors/${resolveId()}`,
                    payload,
                );
                // Adopt what the SERVER stored, not what we sent: it strips
                // whitespace and upper-cases codes, so a draft kept as typed
                // would stay permanently "dirty". A field the SA has changed
                // again since the request left keeps their newer value.
                const next = { ...company.value };
                const committed = { ...committedCompany.value };
                for (const field of Object.keys(payload)) {
                    const confirmed = resp?.data?.[field];
                    let stored =
                        confirmed === undefined ? payload[field] : confirmed;
                    if (stored === null && field !== "timezone") stored = "";
                    committed[field] = stored;
                    if (norm(company.value[field]) === norm(payload[field])) {
                        next[field] = stored;
                    }
                }
                company.value = next;
                committedCompany.value = committed;
                const warnings = resp?.data?.warnings;
                billingSyncWarning.value =
                    Array.isArray(warnings) && warnings.length
                        ? warnings[0]
                        : "";
                if ("name" in payload && norm(next.name) === norm(committed.name)) {
                    // The page header reads this; leaving it stale would
                    // show the old name beside the new one.
                    distributorName.value = next.name;
                }
                return { ok: true };
            } catch (err) {
                return { ok: false, error: describeCompanyFailure(err) };
            }
        }

        function describeNexusFailure(status) {
            if (status === 403 || status === 404) {
                return "Permission denied. You may not edit this field.";
            }
            if (status === 400) {
                return "That value was rejected. Please check it and try again.";
            }
            return "Could not save. Nothing was changed.";
        }

        /**
         * Tick / untick one jurisdiction, then commit it on its own.
         *
         * The stored order follows `stateNames` rather than click order, so
         * the column reads the same however the SA got there -- otherwise a
         * re-tick of the same set would compare as a change. Sends ONLY
         * nexus_states: unsaved Company edits stay pending, never smuggled in.
         */
        async function onNexusChange(names) {
            const next = new Set(names);
            const ordered = stateNames.value.filter((s) => next.has(s));
            nexusDraft.value = ordered;
            const base = nexusCommitted.value;
            if (
                ordered.length === base.length &&
                ordered.every((value, i) => value === base[i])
            ) {
                return;
            }
            nexusSaving.value = true;
            nexusSaved.value = false;
            nexusError.value = "";
            try {
                const resp = await axios.patch(
                    `/api/superadmin/distributors/${resolveId()}`,
                    { nexus_states: ordered },
                );
                const confirmed = Array.isArray(resp?.data?.nexus_states)
                    ? resp.data.nexus_states
                    : [];
                nexusDraft.value = [...confirmed];
                nexusCommitted.value = [...confirmed];
                nexusSaved.value = true;
            } catch (e) {
                // KEEP the ticks the SA made; the baseline does not advance,
                // so the next tick retries.
                nexusError.value = describeNexusFailure(e?.response?.status);
            } finally {
                nexusSaving.value = false;
            }
        }

        function onAccessAction() {
            accessError.value = "";
            if (isDisabled.value) {
                // Enabling restores access -- not the dangerous direction, so
                // it goes straight through without a confirmation.
                applyAccessChange(false);
                return;
            }
            confirmOpen.value = true;
        }

        function cancelAccessAction() {
            confirmOpen.value = false;
        }

        function confirmAccessAction() {
            confirmOpen.value = false;
            applyAccessChange(true);
        }

        async function applyAccessChange(disabled) {
            accessPending.value = true;
            accessError.value = "";
            const id = resolveId();
            try {
                const result = await setDistributorDisabled(id, disabled);
                // Render what the SERVER reports, never what we asked for.
                // The endpoints are idempotent, so `changed` can be false when
                // the tenant was already in the requested state -- and a
                // rejected write must not leave an optimistic lie on screen.
                const nowDisabled = Boolean(result && result.disabled);
                disabledOverride.value = nowDisabled;
            } catch (e) {
                const status = e?.response?.status;
                if (status === 403 || status === 404) {
                    accessError.value =
                        "Permission denied. Only a Super Admin with tenant management rights can enable or disable a distributor.";
                } else {
                    accessError.value = disabled
                        ? "Could not disable the distributor. Nothing was changed."
                        : "Could not enable the distributor. Nothing was changed.";
                }
            } finally {
                accessPending.value = false;
            }
        }


        // ---- Subscription tier -----------------------------------------
        //
        // Jake, 2026-08-31: the DDP could SHOW the tier (in the supplier help
        // text) but not CHANGE it.
        //
        // The plan and the supplier list come from the supplier-overrides
        // response rather than separate fetches, so they can never disagree
        // (design-philosophy principle 4 -- one source). The tier itself is
        // no longer shown: the PLAN drives it (Jake, 2026-10-23).
        //
        // `null` means the distributor has no subscription at all. That is a
        // state the SA must be told about in words, not a select rendered
        // empty -- and it is still assignable: the endpoint creates the
        // subscription.
        const tiers = ref([]);
        const plans = ref([]);
        const planDraft = ref("");
        const planConfirming = ref(false);
        const tierLoading = ref(true);
        const tierSaving = ref(false);
        const tierSaved = ref(false);
        const tierError = ref("");

        // Whether an SA has actually ASSIGNED a plan (a subscription row
        // exists) -- not whether a tier gets RESOLVED via the default-tier
        // fallback. See the supplier-overrides endpoint docstring.
        const tierAssigned = computed(() => supplierAssigned.value);

        // The pinned plan {id, name, is_disabled}, from the SAME response as
        // the tier and the supplier list (one source, principle 4).
        const currentPlan = computed(() => supplierPlan.value);

        function _planLabel(p, { disabled = false } = {}) {
            const marks = [];
            if (p.is_default_signup) marks.push("Default");
            if (p.is_public === false) marks.push("Hidden");
            if (disabled) marks.push("disabled");
            return marks.length ? `${p.name} (${marks.join(", ")})` : p.name;
        }

        // Assignable plans: active ones only, default first, then public by
        // name, then hidden by name. Hidden plans stay assignable -- that is
        // the point of special arrangements. The distributor's OWN plan is
        // always present so the selection never renders blank: if it is
        // disabled (or archived, so absent from the list) it appears labeled
        // "(disabled)" and, being the current value, is a no-op to select.
        const planItems = computed(() => {
            const rank = (p) => (p.is_default_signup ? 0 : p.is_public === false ? 2 : 1);
            const live = plans.value
                .filter((p) => p.is_active !== false)
                .sort(
                    (a, b) =>
                        rank(a) - rank(b) ||
                        String(a.name).localeCompare(String(b.name)),
                );
            const items = live.map((p) => ({
                value: p.id,
                name: p.name,
                label: _planLabel(p),
            }));
            const cur = currentPlan.value;
            if (cur && !live.some((p) => p.id === cur.id)) {
                const known = plans.value.find((p) => p.id === cur.id);
                items.push({
                    value: cur.id,
                    name: cur.name,
                    label: _planLabel(
                        { ...(known || {}), name: cur.name },
                        { disabled: true },
                    ),
                });
            }
            return [{ value: "", label: "-- Select a plan --" }, ...items];
        });

        // Save is a real change only when the draft differs from the pinned
        // plan; with no subscription any pick is a real write.
        const planDirty = computed(() => {
            if (planDraft.value === "") return false;
            return !(
                currentPlan.value && planDraft.value === currentPlan.value.id
            );
        });

        const draftPlanName = computed(() => {
            const hit = planItems.value.find((i) => i.value === planDraft.value);
            return hit ? hit.name || hit.label : "";
        });

        // Resting selection = the plan the server says the distributor is on.
        function syncPlanDraft() {
            planDraft.value = currentPlan.value ? currentPlan.value.id : "";
        }

        // A "Saved." confirmation must not outlive the state it described;
        // bound to the select's own event, not a watch (the post-save sync
        // would clear it).
        function onPlanPicked(value) {
            planDraft.value = value;
            tierSaved.value = false;
            planConfirming.value = false;
        }

        function requestPlanChange() {
            if (!planDirty.value) return;
            tierError.value = "";
            planConfirming.value = true;
        }

        function cancelPlanChange() {
            planConfirming.value = false;
            syncPlanDraft();
        }

        async function loadTiers() {
            tierLoading.value = true;
            try {
                const [tierResp, planResp] = await Promise.all([
                    listTiers(),
                    listPlans(),
                ]);
                tiers.value = tierResp?.data?.tiers || [];
                plans.value = planResp?.data?.plans || [];
            } catch (e) {
                tiers.value = [];
                plans.value = [];
                tierError.value = "Could not load the list of plans.";
            } finally {
                tierLoading.value = false;
            }
        }

        async function savePlan() {
            if (!planDirty.value) return;
            tierSaving.value = true;
            tierSaved.value = false;
            tierError.value = "";
            try {
                await reassignDistributorPlan(resolveId(), {
                    plan_id: planDraft.value,
                });
                // Re-read: refreshes the plan, the tier and the supplier list
                // together from the one response.
                await loadSupplierOverrides();
                planConfirming.value = false;
                tierSaved.value = true;
            } catch (e) {
                const status = e?.response?.status;
                // The server says WHY it refused (e.g. a plan with no
                // version to pin); show that rather than a generic line.
                const rawReason = e?.response?.data?.message;
                const serverReason =
                    typeof rawReason === "string"
                        ? rawReason.trim().replace(/\.+$/, "")
                        : "";
                const message =
                    status === 403
                        ? "Permission denied. Only a Super Admin can change a distributor's plan."
                        : serverReason
                          ? `Could not change the plan: ${serverReason}. Nothing was changed.`
                          : "Could not change the plan. Nothing was changed.";
                // Re-read FIRST, then set the message -- the re-read clears
                // errors and would swallow one set before it.
                await loadSupplierOverrides();
                planConfirming.value = false;
                tierError.value = message;
            } finally {
                tierSaving.value = false;
            }
        }

        // ---- Stripe platform mode (dual-mode unit 5) --------------------
        //
        // `stripeAccountConnected` and `stripeMode` come from ONE server
        // response. Whether to lock the control and whether the endpoint will
        // refuse the write are the same question, and answering it from two
        // reads is how a UI and its guard drift apart -- the drift surfaces as
        // a control that looks usable and 409s.
        //
        // Not read from the distributor resource above: `stripe_platform_mode`
        // is in `Distributor.exclude_attrs`, so SAFRS serializes it on no GET.
        const stripeMode = ref("");
        const stripeAccountConnected = ref(false);
        const stripeModeDraft = ref("");
        const stripeModeLoading = ref(true);
        const stripeModeSaving = ref(false);
        const stripeModeSaved = ref(false);
        const stripeModeError = ref("");
        // The server's stranded-order count while its warning is up; null
        // when there is no pending warning. See the template comment.
        const stripeModeUncollectable = ref(null);

        // The empty sentinel is the resting state, not a choice: it keeps Save
        // disabled until the SA actually picks something, so an idle page
        // cannot be one stray click from repointing a distributor's platform.
        // Same posture as the tier picker above.
        const stripeModeItems = [
            { value: "", label: "-- Select a platform --" },
            { value: "sandbox", label: "Sandbox" },
            { value: "live", label: "Live" },
        ];

        function _stripeModeLabel(value) {
            const match = stripeModeItems.find((i) => i.value === value);
            // Fall back to the raw value rather than blank: a mode the UI does
            // not recognize is still the mode this distributor is on, and
            // blanking it would hide that.
            return match && value ? match.label : value || "unknown";
        }

        const stripeModeLabel = computed(() =>
            _stripeModeLabel(stripeMode.value),
        );

        const stripeModeDirty = computed(() => {
            if (stripeModeDraft.value === "") return false;
            return stripeModeDraft.value !== stripeMode.value;
        });

        // Bound to the select's own event rather than a watch on the draft:
        // `saveStripeMode` resets the draft to "" on success, and a watcher
        // could not tell that reset apart from the user picking something new
        // -- it would fire on the reset and clear the "Saved." it had just
        // set. Same bug the tier control documents.
        function onStripeModePicked(value) {
            stripeModeDraft.value = value;
            stripeModeSaved.value = false;
            // The warning was about the PREVIOUS pick; a confirm must never
            // carry its count over to a different change.
            stripeModeUncollectable.value = null;
        }

        function _applyStripeModePayload(data) {
            stripeMode.value = data?.stripe_platform_mode || "";
            stripeAccountConnected.value = Boolean(
                data?.stripe_account_connected,
            );
        }

        async function loadStripeMode() {
            stripeModeLoading.value = true;
            const id = resolveId();
            if (id === null) {
                stripeModeLoading.value = false;
                return;
            }
            try {
                const resp = await getStripePlatformMode(id);
                _applyStripeModePayload(resp?.data);
            } catch (e) {
                // Fail CLOSED. An unreadable connection state must not render
                // as "nothing connected" -- that is the one wrong answer here,
                // because it would offer the control precisely when we cannot
                // prove the change is safe. The backend still refuses, but the
                // page should not invite the attempt.
                stripeAccountConnected.value = true;
                stripeModeError.value =
                    "Could not load the Stripe platform mode.";
            } finally {
                stripeModeLoading.value = false;
            }
        }

        async function saveStripeMode(confirmCount) {
            if (!stripeModeDirty.value) return;
            stripeModeSaving.value = true;
            stripeModeSaved.value = false;
            stripeModeError.value = "";
            try {
                const resp = await setStripePlatformMode(
                    resolveId(),
                    stripeModeDraft.value,
                    // Only a number is a confirmation. The Save button's
                    // click handler passes its DOM event here, which must
                    // not be sent as consent.
                    typeof confirmCount === "number" ? confirmCount : undefined,
                );
                // Render the SERVER's answer, not the local draft -- only the
                // server knows what was actually stored.
                _applyStripeModePayload(resp?.data);
                stripeModeDraft.value = "";
                stripeModeUncollectable.value = null;
                stripeModeSaved.value = true;
            } catch (e) {
                const status = e?.response?.status;
                const data = e?.response?.data;
                if (status === 409 && data?.error === "uncollectable_orders") {
                    // A warning, not a failure: nothing changed and the
                    // admin decides. Our view of the mode is not stale, so
                    // no re-read.
                    stripeModeUncollectable.value =
                        data.uncollectable_order_count;
                    return;
                }
                stripeModeUncollectable.value = null;
                let message;
                if (status === 409 && data?.error === "live_platform_unconfigured") {
                    // This SERVER holds no live Stripe key, so a live
                    // distributor would stop it booting. Nothing is
                    // connected; the disconnect message below would be wrong.
                    message =
                        data.message ||
                        "Live Stripe is not configured on this server. " +
                            "Nothing was changed.";
                } else if (status === 409) {
                    // The safety rule fired. This is reachable even though the
                    // control was offered: an account can be connected in
                    // another tab between the read and the write. Say what to
                    // do, not just that it failed.
                    message =
                        "This distributor now has a Stripe account connected. " +
                        "Disconnect it first, then change the platform. " +
                        "Nothing was changed.";
                } else if (status === 403 || status === 404) {
                    // 404 is the endpoint's existence-oracle posture for a
                    // non-SA caller, so to THIS user it means the same thing a
                    // 403 does. Collapsing them here is correct; collapsing
                    // either into the 409 above would not be, because the next
                    // action differs.
                    message =
                        "Permission denied. Only a Super Admin can change a " +
                        "distributor's Stripe platform.";
                } else {
                    message =
                        "Could not change the Stripe platform. Nothing was " +
                        "changed.";
                }
                // Re-read FIRST, then set the message: `loadStripeMode` clears
                // nothing here but DOES overwrite the connection state, and a
                // 409 means our copy of it was stale. Setting the message
                // after keeps it on screen. Same ordering as savePlan.
                await loadStripeMode();
                stripeModeError.value = message;
            } finally {
                stripeModeSaving.value = false;
            }
        }

        function confirmStripeModeUncollectable() {
            return saveStripeMode(stripeModeUncollectable.value);
        }

        function cancelStripeModeUncollectable() {
            stripeModeUncollectable.value = null;
        }

        // ---- Supplier availability override ----------------------------
        //
        // `supplierRows` mirrors the server's shape: each row carries
        // `tier_enabled` (the platform default), `override` (null when the
        // row inherits) and `effective` (what the resolver answers). The
        // checkbox binds to `effective` because that is the question an SA
        // is asking -- "can they sell this?" -- and the row separately shows
        // what it is overriding.
        //
        // `supplierDrafts` holds only the CHANGES, keyed by supplier_key,
        // with `null` meaning "clear the override". Saving the whole row set
        // instead would write an override for every supplier on the page,
        // turning a single grant into a permanent detachment from the tier
        // for all of them.
        const supplierRows = ref([]);
        const supplierTierKey = ref(null);
        const supplierPlan = ref(null);
        // Whether a subscription EXISTS, which is a different question from
        // what `supplierTierKey` resolves to -- see `tierAssigned`.
        const supplierAssigned = ref(false);
        const supplierLoading = ref(true);
        const supplierSaving = ref(false);
        const supplierSaved = ref(false);
        const supplierError = ref("");
        const supplierDrafts = ref({});

        const supplierDirty = computed(
            () => Object.keys(supplierDrafts.value).length > 0
        );

        async function loadSupplierOverrides() {
            supplierLoading.value = true;
            supplierError.value = "";
            try {
                const resp = await getDistributorSupplierOverrides(resolveId());
                supplierRows.value = resp.data.suppliers || [];
                supplierTierKey.value = resp.data.tier_key || null;
                supplierPlan.value = resp.data.plan || null;
                syncPlanDraft();
                supplierAssigned.value = Boolean(resp.data.assigned);
                supplierDrafts.value = {};
            } catch (e) {
                supplierError.value =
                    "Could not load supplier availability for this distributor.";
            } finally {
                supplierLoading.value = false;
            }
        }

        function onSupplierToggle(supplier, event) {
            const next = event.target.checked;
            supplier.effective = next;
            supplier.override = next;
            supplierDrafts.value = {
                ...supplierDrafts.value,
                [supplier.supplier_key]: next,
            };
            supplierSaved.value = false;
        }

        function clearSupplierOverride(supplier) {
            // Back to inheriting: the checkbox immediately shows the TIER's
            // answer, so the consequence of clearing is visible before Save
            // rather than after.
            supplier.override = null;
            supplier.effective = supplier.tier_enabled;
            supplierDrafts.value = {
                ...supplierDrafts.value,
                [supplier.supplier_key]: null,
            };
            supplierSaved.value = false;
        }

        async function saveSupplierOverrides() {
            if (!supplierDirty.value) return;
            supplierSaving.value = true;
            supplierSaved.value = false;
            supplierError.value = "";
            try {
                await setDistributorSupplierOverrides(
                    resolveId(),
                    supplierDrafts.value
                );
                // Re-read rather than trust the local copy: `effective` is
                // the RESOLVER's answer, and only the server can compute it.
                await loadSupplierOverrides();
                supplierSaved.value = true;
            } catch (e) {
                const status = e?.response?.status;
                const message =
                    status === 403
                        ? "Permission denied. Only a Super Admin can override supplier availability."
                        : "Could not save supplier availability. Nothing was changed.";
                // Re-read FIRST, then set the message. `loadSupplierOverrides`
                // clears `supplierError` on entry, so setting the message
                // before it would leave the user with a silently failed save
                // and no explanation -- caught by
                // ddp-supplier-override.spec.js on 2026-08-27.
                await loadSupplierOverrides();
                supplierError.value = message;
            } finally {
                supplierSaving.value = false;
            }
        }

        function cancel() {
            if (typeof window !== "undefined" && window.location) {
                window.location.href = "/admin/distributors";
            }
        }

        onMounted(load);
        onMounted(loadSupplierOverrides);
        onMounted(loadTiers);
        onMounted(loadStripeMode);

        const stripeBindingsDistributorId = computed(() => resolveId());

        return {
            loading,
            loadError,
            distributorName,
            cancel,
            // Company -- shared CompanyForm, one Save
            companyForm,
            company,
            canEditCompany,
            billingSyncWarning,
            resolvedDistributorId,
            onCompanyField,
            saveCompany,
            // Nexus -- auto-save on change
            nexusDraft,
            nexusSaved,
            nexusError,
            stateNames,
            onNexusChange,
            // Tenant access (enable / disable)
            isDisabled,
            canManageTenants,
            confirmOpen,
            accessPending,
            accessError,
            onAccessAction,
            cancelAccessAction,
            confirmAccessAction,
            // Subscription tier
            planDraft,
            planConfirming,
            planItems,
            planDirty,
            draftPlanName,
            currentPlan,
            tierLoading,
            tierSaving,
            tierSaved,
            tierError,
            tierAssigned,
            onPlanPicked,
            requestPlanChange,
            cancelPlanChange,
            savePlan,
            // Stripe platform mode
            stripeAccountConnected,
            stripeModeDraft,
            stripeModeItems,
            stripeModeLabel,
            stripeModeLoading,
            stripeModeSaving,
            stripeModeSaved,
            stripeModeError,
            stripeModeDirty,
            onStripeModePicked,
            saveStripeMode,
            stripeModeUncollectable,
            stripeBindingsDistributorId,
            loadStripeMode,
            confirmStripeModeUncollectable,
            cancelStripeModeUncollectable,
            // Supplier availability override
            supplierRows,
            supplierTierKey,
            supplierLoading,
            supplierSaving,
            supplierSaved,
            supplierError,
            supplierDirty,
            onSupplierToggle,
            clearSupplierOverride,
            saveSupplierOverrides,
        };
    },
};
</script>

<style scoped>
.distributor-edit {
    margin-bottom: 24px;
}
.distributor-edit__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.distributor-edit__title {
    font-size: 22px;
    font-weight: 600;
    margin: 0;
    color: #1a202c;
}
.distributor-edit__tier {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
}
.distributor-edit__tier-controls {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 12px;
}
.distributor-edit__tier-select {
    border: 1px solid #cbd5e0;
    border-radius: 4px;
    padding: 6px 8px;
    font-size: 14px;
    min-width: 220px;
    background: #fff;
}
.distributor-edit__tier-none {
    /* The no-tier state is a consequence an SA needs to notice, not a
       neutral fact -- but it is not an ERROR either (nothing failed), so it
       is weighted rather than alarmed. */
    font-weight: 600;
    color: #744210;
    background: #fffff0;
    border: 1px solid #f6e05e;
    border-radius: 4px;
    padding: 8px 12px;
}
.distributor-edit__suppliers {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid #e2e8f0;
}
.distributor-edit__supplier-table {
    width: 100%;
    max-width: 640px;
    border-collapse: collapse;
    margin-bottom: 12px;
}
.distributor-edit__supplier-table th,
.distributor-edit__supplier-table td {
    text-align: left;
    padding: 8px 12px 8px 0;
    border-bottom: 1px solid #edf2f7;
    font-size: 14px;
}
.distributor-edit__muted {
    /* Token, not a raw hex: the raw-grey-text-contrast guard rejects
       sub-AA greys, and #718096 fails it. --tf-fg-4 is the "hints" role,
       matching .sa-muted on the sibling tier page. */
    color: var(--tf-fg-4);
    font-size: 12px;
}
.distributor-edit__link-btn {
    background: none;
    border: none;
    color: #2b6cb0;
    cursor: pointer;
    padding: 0;
    font-size: 13px;
    text-decoration: underline;
}
.distributor-edit__error {
    color: #c53030;
    background: #fff5f5;
    border: 1px solid #feb2b2;
    border-radius: 4px;
    padding: 8px 12px;
    margin-bottom: 12px;
}
.distributor-edit__saved {
    color: #276749;
    font-size: 13px;
    margin-left: 12px;
}
.distributor-edit__row {
    margin-bottom: 16px;
    max-width: 420px;
}
.distributor-edit__label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #2d3748;
    margin-bottom: 6px;
}
.distributor-edit__readonly {
    font-size: 15px;
    color: #1a202c;
    margin: 0;
}
.distributor-edit__hint {
    font-size: 13px;
    color: #4b5563;
    margin: 0 0 8px 0;
    line-height: 1.4;
}
.distributor-edit__feedback {
    margin: 8px 0;
}
.distributor-edit__access {
    margin-top: 32px;
    padding-top: 20px;
    border-top: 1px solid #e5e7eb;
    max-width: 560px;
}
/* CompanyForm block headings carry the global .md-header size, which is
   sized for the Owner full-page form. Scale them to this page sections. */
.distributor-edit__form :deep(.md-header) {
    font-size: 14px;
    font-weight: 600;
    margin: 16px 0 6px 0;
    color: #4a5568;
}
.distributor-edit__access-title {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 8px 0;
    color: #1a202c;
}
.distributor-edit__actions {
    margin-top: 16px;
}
</style>
